import * as React from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Returning to a list puts the member back where they were (`MEM-042` AC-6).
 *
 * ## Why the story bothers to say this
 *
 * *"A member working down a match list, opening each, and being returned to the top every time
 * will stop opening them."* The whole of matches, connections and follow-up is a list you work
 * down — open, read, back, open the next — and losing your place makes the fourth one cost more
 * than the first. It is the difference between a screen that feels finished and one that does not.
 *
 * ## Why not `<ScrollRestoration>`
 *
 * React Router's own component needs a data router (`createBrowserRouter`). This app renders
 * `<Routes>` inside a `BrowserRouter`, and converting the whole router to get one behaviour is a
 * larger change than the behaviour is worth — every route in `App.tsx` would move.
 *
 * ## Only on the way back, and that is the point
 *
 * Restoring on *every* visit would be wrong: a member tapping Matches from the tab bar wants the
 * top of the list, not wherever they were an hour ago. `useNavigationType()` distinguishes them —
 * `POP` is the browser's back, everything else is a fresh navigation.
 *
 * ## The retry loop is not defensive padding
 *
 * The list is not there when this runs. Restoring 900px on a page that is currently 400px tall
 * silently does nothing — the browser clamps to the bottom — and the member lands near the top
 * anyway, which looks exactly like the bug this exists to fix. So the restore is attempted across
 * a few frames until the document is tall enough to honour it, then stops. It gives up rather than
 * looping: a list that came back shorter (an item removed, a filter still applied) will never be
 * tall enough, and a hook that spins forever waiting for that is worse than one that lands high.
 */

/**
 * Module-scoped rather than `sessionStorage`, deliberately.
 *
 * `MEM-042` AC-1 puts the source of truth on the server — *"nothing about a member's connections,
 * matches or follow-ups may live in local storage as the source of truth"*. A scroll offset is
 * not that; it is a property of this tab's history, and it should die with the tab. Persisting it
 * would mean a member who closes the app on Tuesday and opens it on Thursday lands halfway down a
 * list for no reason they could explain.
 */
const positions = new Map<string, number>();

/**
 * How long to keep trying before accepting the page will not get taller.
 *
 * This replaced a count of animation frames, and the difference is the whole behaviour. Ten frames
 * is about 160ms, which is plenty for a page that renders from memory and nowhere near enough for
 * one whose list arrives over the network: the document sits at exactly viewport height while the
 * request is in flight, every `scrollTo` clamps to zero, and the budget is spent before there is
 * anything to scroll. It restored correctly against a warm cache and silently failed against a
 * cold one — the same code, passing and failing on timing, which is the worst way for this to be
 * wrong.
 *
 * A deadline in milliseconds is honest about what is being waited for. It exists at all because a
 * list that comes back **shorter** — an item removed, a filter still applied — will never be tall
 * enough, and waiting forever for that is worse than landing high.
 */
export const RESTORE_TIMEOUT_MS = 1500;

/** What should happen to the scroll position on arriving at a route. */
export type ScrollIntent = { readonly kind: 'top' } | { readonly kind: 'restore'; readonly to: number };

/**
 * The decision, separated from the doing.
 *
 * There is no DOM renderer in this repository, so a hook cannot be rendered in a test. Extracting
 * the *decision* is how every other screen in this app is made testable — a function over data in
 * `constants.ts` — and this is the same move. What is left in the hook is the part that talks to
 * the browser, which is checked by reading it and by driving it.
 *
 * The rule is one sentence: **restore only on the way back.** A member tapping Matches in the bar
 * wants the top of the list; a member pressing back from a match wants where they were. Anything
 * else — a fresh navigation, a route with nothing remembered, a remembered position of zero —
 * is the top, and `0` is deliberately not special-cased into a restore because scrolling to the
 * top and restoring to the top are the same thing done twice.
 */
export function decideScrollIntent(input: {
  readonly navigationType: string;
  readonly saved: number | undefined;
}): ScrollIntent {
  if (input.navigationType !== 'POP') return { kind: 'top' };
  if (input.saved === undefined || input.saved <= 0) return { kind: 'top' };
  return { kind: 'restore', to: input.saved };
}

export function useScrollMemory(): void {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  // **Recorded as the member scrolls, not as they leave.**
  //
  // The obvious implementation saves the offset in the effect's cleanup, and it silently records
  // zero. By the time cleanup runs, React has already swapped in the next screen; if that screen
  // is shorter — and a detail usually is — the browser has clamped `scrollY` to fit it. The list
  // is remembered at the top, every time, and the feature looks implemented.
  //
  // That is exactly what the first version of this file did. It passed a browser check too,
  // because the browser's own `scrollRestoration` was quietly doing the work; turning that off is
  // what showed the hook contributing nothing.
  // `useLayoutEffect`, not `useEffect`, and this is the second thing that has to be right.
  //
  // A passive effect's cleanup runs *after* paint. Between React committing the new screen and
  // React removing this listener, the browser has already laid out the shorter page, clamped
  // `scrollY` to fit it, and fired a scroll event — which the still-attached listener records
  // against the list the member is leaving. A layout effect's cleanup runs during the commit,
  // before any of that, so the listener is gone before there is a clamped value to hear about.
  React.useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;
    // **Synchronously, and that is the whole trick.**
    //
    // The obvious optimisation is to coalesce these into a `requestAnimationFrame`, and it breaks
    // the feature: navigating away shrinks the document, the browser clamps `scrollY` and fires a
    // scroll event, and a deferred callback reads the clamped zero and stores *that* against the
    // list. The frame of delay is exactly long enough for the value to be destroyed before it is
    // read. A `Map.set` of a number is cheap enough that there is nothing to coalesce.
    const record = (): void => {
      positions.set(pathname, window.scrollY);
    };
    window.addEventListener('scroll', record, { passive: true });
    return () => {
      window.removeEventListener('scroll', record);
    };
  }, [pathname]);

  React.useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // **The browser is told to stop doing this itself.** Native restoration fires before an async
    // list has rendered, so it restores against a short document, gets clamped, and drops the
    // member near the top — which is the bug this hook exists to fix, arriving from the other
    // direction. Two mechanisms both trying is worse than either alone.
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    const intent = decideScrollIntent({ navigationType, saved: positions.get(pathname) });
    if (intent.kind === 'top') {
      // A fresh navigation starts at the top. Without this, opening a detail from halfway down a
      // list leaves the detail scrolled to the middle of itself.
      window.scrollTo(0, 0);
      return () => {
        window.history.scrollRestoration = previous;
      };
    }

    // **Driven by the page growing, not by a timer ticking.**
    //
    // `scrollY` lands short while the list is still rendering — the document is viewport-height,
    // so the browser clamps every attempt to zero. Rather than guess how many frames that lasts,
    // watch for the document changing size and try again each time it does. The deadline is the
    // backstop for the case that never resolves.
    const target = intent.to;
    let settled = false;
    let observer: ResizeObserver | undefined;
    let deadline = 0;

    const stop = (): void => {
      settled = true;
      observer?.disconnect();
      window.clearTimeout(deadline);
    };

    const attempt = (): void => {
      if (settled) return;
      window.scrollTo(0, target);
      if (Math.abs(window.scrollY - target) <= 1) stop();
    };

    deadline = window.setTimeout(stop, RESTORE_TIMEOUT_MS);
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(attempt);
      observer.observe(document.documentElement);
    }
    attempt();

    return () => {
      stop();
      window.history.scrollRestoration = previous;
    };
  }, [pathname, navigationType]);
}

/** Test seam. Nothing in the app calls this; it exists so a test can start from a known state. */
export function forgetScrollPositions(): void {
  positions.clear();
}
